// components/auth/RegisterForm.jsx
import React, { useState } from 'react';
import { 
  User, Mail, Phone, Lock, Building, Shield, FileText, 
  Eye, EyeOff, UserPlus, AlertCircle 
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const RegisterForm = ({ onSwitchToLogin }) => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '', 
    company: '', role: 'user',
    gdprServiceConsent: false,
    gdprMarketingConsent: false,
    gdprProfilingConsent: false
  });
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const { register } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setFieldErrors({});

    if (form.password !== form.confirmPassword) {
      setError('Le password non coincidono');
      setBusy(false);
      return;
    }

    if (!form.gdprServiceConsent) {
      setError('Devi accettare i termini del servizio per registrarti');
      setBusy(false);
      return;
    }

    const res = await register(form);
    if (!res.success) {
      setError(res.message);
      if (res.errors) {
        const errs = {};
        res.errors.forEach(er => { errs[er.param] = er.msg; });
        setFieldErrors(errs);
      }
    }
    setBusy(false);
  };

  const inputCls = (name) =>
    `w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent ${
      fieldErrors[name] ? 'border-red-300' : 'border-gray-300'
    }`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <UserPlus className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Crea Account</h1>
          <p className="text-gray-600 mt-1">Registrati per iniziare</p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          {/* Informazioni Base */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
              <div className="relative">
                <User className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  required
                  className={inputCls('name')}
                  placeholder="Il tuo nome"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                />
              </div>
              {fieldErrors.name && <p className="text-red-500 text-sm mt-1">{fieldErrors.name}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <div className="relative">
                <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="email"
                  required
                  className={inputCls('email')}
                  placeholder="La tua email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                />
              </div>
              {fieldErrors.email && <p className="text-red-500 text-sm mt-1">{fieldErrors.email}</p>}
            </div>
          </div>

          {/* Telefono */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
            <div className="relative">
              <Phone className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="tel"
                className={inputCls('phone')}
                placeholder="+39 XXX XXXXXXX"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            {fieldErrors.phone && <p className="text-red-500 text-sm mt-1">{fieldErrors.phone}</p>}
            <p className="text-xs text-gray-500 mt-1">Formato italiano accettato</p>
          </div>

          {/* Password */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
              <div className="relative">
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPwd ? 'text' : 'password'}
                  required
                  className={inputCls('password') + ' pr-12'}
                  placeholder="Crea una password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {fieldErrors.password && <p className="text-red-500 text-sm mt-1">{fieldErrors.password}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Conferma password *</label>
              <div className="relative">
                <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type={showPwd2 ? 'text' : 'password'}
                  required
                  className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Conferma password"
                  value={form.confirmPassword}
                  onChange={e => setForm({ ...form, confirmPassword: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd2(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPwd2 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500">Password: min 8 caratteri, con maiuscola, minuscola e numero</p>

          {/* Informazioni Aggiuntive */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Azienda</label>
              <div className="relative">
                <Building className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Nome azienda"
                  value={form.company}
                  onChange={e => setForm({ ...form, company: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo di account</label>
              <div className="relative">
                <Shield className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <select
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none bg-white"
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value })}
                >
                  <option value="user">Ospite (solo prenotazioni)</option>
                  <option value="admin">Amministratore (gestione eventi)</option>
                  <option value="collaborator">Collaboratore (staff eventi)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Consensi GDPR */}
          <div className="bg-gray-50 p-6 rounded-xl space-y-4">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Consensi Privacy (GDPR)
            </h3>
            
            {/* Consenso Obbligatorio */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="gdprService"
                checked={form.gdprServiceConsent}
                onChange={e => setForm({ ...form, gdprServiceConsent: e.target.checked })}
                className="mt-1 w-4 h-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="gdprService" className="text-sm text-gray-700">
                <span className="font-medium text-red-600">*</span> Accetto i{' '}
                <button type="button" className="text-green-600 hover:underline font-medium">
                  Termini del Servizio
                </button>{' '}
                e l'
                <button type="button" className="text-green-600 hover:underline font-medium">
                  Informativa Privacy
                </button>
                . Consenso necessario per fornire il servizio.
              </label>
            </div>

            {/* Consensi Opzionali */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="gdprMarketing"
                checked={form.gdprMarketingConsent}
                onChange={e => setForm({ ...form, gdprMarketingConsent: e.target.checked })}
                className="mt-1 w-4 h-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="gdprMarketing" className="text-sm text-gray-700">
                Acconsento al trattamento dei dati per finalità di marketing e comunicazioni promozionali.
                <span className="text-gray-500 block">Puoi revocarlo in qualsiasi momento.</span>
              </label>
            </div>

            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="gdprProfiling"
                checked={form.gdprProfilingConsent}
                onChange={e => setForm({ ...form, gdprProfilingConsent: e.target.checked })}
                className="mt-1 w-4 h-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="gdprProfiling" className="text-sm text-gray-700">
                Acconsento alla profilazione per migliorare l'esperienza d'uso e personalizzare i contenuti.
                <span className="text-gray-500 block">Puoi revocarlo in qualsiasi momento.</span>
              </label>
            </div>

            <p className="text-xs text-gray-500">
              I consensi opzionali possono essere modificati in qualsiasi momento dalle impostazioni del profilo.
            </p>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
          >
            {busy ? (
              <div className="animate-spin rounded-full border-2 border-white border-t-transparent w-5 h-5" />
            ) : (
              <UserPlus className="w-5 h-5" />
            )}
            {busy ? 'Registrazione in corso...' : 'Registrati'}
          </button>
        </form>

        <div className="text-center mt-6">
          <p className="text-gray-600">
            Hai già un account?{' '}
            <button onClick={onSwitchToLogin} className="text-green-600 hover:text-green-800 font-semibold">
              Accedi
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;